import type { Store } from '@reduxjs/toolkit';
import {
  clearStoredState,
  readStoredState,
  writeStoredState
} from '../pages/CaseDetails/feedbackForm/component/CallBridge/WhatsappCalls/callStateStorage';
import {
  getTabId,
  isBlockingStatus
} from '../pages/CaseDetails/feedbackForm/component/CallBridge/WhatsappCalls/utils';
import { WhatsappCallReduxState } from './whatsappCallSlice';

// Subscribes to the Redux store and reflects whatsappCall state into localStorage using the existing writeStoredState / clearStoredState helpers.
// This place that mutates localStorage from Redux changes.
export const subscribeWhatsappCallPersistence = (store: Store): void => {
  let prevStatus: string | null = null;
  let prevError: string | null = null;
  let prevPendingPhone: string | null = null;
  let prevTabSharingStatus: string | null = null;
  let prevIsCallCompleted: boolean | null = null;
  let prevLoanId: string | null = null;
  let prevCustomerId: string | null = null;

  store.subscribe(() => {
    const state = store.getState();
    const cur: WhatsappCallReduxState | undefined = state?.whatsappCall;
    if (!cur) return;

    const unchanged =
      cur.status === prevStatus &&
      cur.error === prevError &&
      cur.pendingPhone === prevPendingPhone &&
      cur.tabSharingStatus === prevTabSharingStatus &&
      cur.isCallCompleted === prevIsCallCompleted &&
      cur.callLoanId === prevLoanId &&
      cur.callCustomerId === prevCustomerId;

    if (unchanged) return;

    prevStatus = cur.status;
    prevError = cur.error;
    prevPendingPhone = cur.pendingPhone;
    prevTabSharingStatus = cur.tabSharingStatus;
    prevIsCallCompleted = cur.isCallCompleted;
    prevLoanId = cur.callLoanId;
    prevCustomerId = cur.callCustomerId;

    // Non-blocking statuses → clear our owned snapshot, but only when the snapshot actually matches the customer/loan that just went non-blocking (or Redux was fully reset to empty).
    // This prevents a stray reset for customer B from clearing customer A's active snapshot.
    if (!isBlockingStatus(cur.status)) {
      const stored = readStoredState();
      if (
        stored &&
        stored.ownerTabId === getTabId() &&
        (cur.callLoanId === '' ||
          (stored.loanId === cur.callLoanId && stored.customerId === cur.callCustomerId))
      ) {
        clearStoredState();
      }
      return;
    }

    // Blocking status → persist a snapshot owned by this tab.
    writeStoredState({
      status: cur.status,
      error: cur.error,
      isCallCompleted: cur.isCallCompleted,
      pendingPhone: cur.pendingPhone,
      active: true,
      loanId: cur.callLoanId,
      customerId: cur.callCustomerId,
      tabSharingStatus: cur.tabSharingStatus
    });
  });
};
